import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWishlist } from '@/context/WishlistContext';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { 
  Heart, ShoppingCart, Trash2, ShoppingBag, ArrowLeft, 
  Share2, Package, Star, Flame, AlertTriangle, Check, Plus, Minus, Truck
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const WishlistPage = () => {
  const navigate = useNavigate();
  const { wishlist, loading, removeFromWishlist, fetchWishlist } = useWishlist();
  const { addToCart, getItemQuantity, updateQuantity } = useCart();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);

  useEffect(() => {
    fetchWishlist();
  }, [fetchWishlist]);

  const handleRemove = async (productId) => {
    const success = await removeFromWishlist(productId);
    if (success) {
      toast.success('Removed from wishlist');
      setSelectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  const handleAddToCart = (product) => {
    if (product.stock <= 0) {
      toast.error('This product is out of stock');
      return;
    }
    addToCart(product, 1);
    toast.success(`${product.name} added to cart!`);
  };

  const handleIncrement = (product, e) => {
    e.stopPropagation();
    const qty = getItemQuantity(product.id);
    if (qty >= product.stock) {
      toast.error(`Only ${product.stock} items available`);
      return;
    }
    addToCart(product);
  };

  const handleDecrement = (product, e) => {
    e.stopPropagation();
    const qty = getItemQuantity(product.id);
    updateQuantity(product.id, qty - 1);
  };

  const handleMoveToCart = async (product) => {
    if (product.stock <= 0) {
      toast.error('This product is out of stock');
      return;
    }
    addToCart(product, 1);
    await removeFromWishlist(product.id);
    toast.success(`${product.name} moved to cart!`);
  };

  const handleAddAllToCart = () => {
    const inStockItems = wishlist.filter(p => p.stock > 0);
    if (inStockItems.length === 0) {
      toast.error('No items in stock to add');
      return;
    }
    inStockItems.forEach(product => {
      addToCart(product, 1);
    });
    toast.success(`${inStockItems.length} item${inStockItems.length > 1 ? 's' : ''} added to cart!`);
  };

  const handleRemoveSelected = async () => {
    if (selectedItems.size === 0) return;
    
    for (const productId of selectedItems) {
      await removeFromWishlist(productId);
    }
    toast.success(`${selectedItems.size} item${selectedItems.size > 1 ? 's' : ''} removed`);
    setSelectedItems(new Set());
    setSelectMode(false);
  };

  const toggleSelectItem = (productId) => {
    setSelectedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === wishlist.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(wishlist.map(p => p.id)));
    }
  };

  // Get stock badge
  const getStockBadge = (product) => {
    if (product.stock <= 0) {
      return { text: 'Out of Stock', color: 'bg-red-500', icon: Package };
    }
    if (product.stock <= 3) {
      return { text: `Only ${product.stock} Left!`, color: 'bg-orange-500', icon: AlertTriangle };
    }
    if (product.stock <= 10) {
      return { text: 'Low Stock', color: 'bg-amber-500', icon: AlertTriangle };
    }
    if (product.avg_rating >= 4 || product.rating_count > 5) {
      return { text: 'Selling Fast', color: 'bg-rose-500', icon: Flame };
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="h-8 w-32 bg-slate-200 rounded animate-pulse mb-8" />
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={`wishlist-skeleton-${i}`} className="bg-white rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-square bg-slate-200" />
                <div className="p-4 space-y-3">
                  <div className="h-3 bg-slate-200 rounded w-1/3" />
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-6 bg-slate-200 rounded w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          className="mb-4 text-slate-600 hover:text-slate-900 -ml-2"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back
        </Button>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-outfit font-bold tracking-tight text-slate-900 flex items-center gap-3">
              <Heart className="w-7 h-7 sm:w-8 sm:h-8 text-red-500 fill-red-500" />
              My Wishlist
            </h1>
            <p className="text-slate-500 mt-1 text-sm sm:text-base">
              {wishlist.length} item{wishlist.length !== 1 ? 's' : ''} saved
            </p>
          </div>
          
          {wishlist.length > 0 && (
            <div className="flex gap-2">
              {selectMode ? (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="text-xs sm:text-sm"
                  >
                    {selectedItems.size === wishlist.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleRemoveSelected}
                    disabled={selectedItems.size === 0}
                    className="text-xs sm:text-sm"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Remove ({selectedItems.size})
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectMode(false);
                      setSelectedItems(new Set());
                    }}
                    className="text-xs sm:text-sm"
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectMode(true)}
                    className="text-xs sm:text-sm"
                  >
                    Select
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleAddAllToCart}
                    className="bg-[#0047AB] hover:bg-[#003380] text-xs sm:text-sm"
                  >
                    <ShoppingCart className="w-4 h-4 mr-1" />
                    Add All to Cart
                  </Button>
                </>
              )}
            </div>
          )}
        </div>

        {wishlist.length === 0 ? (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-sm p-8 sm:p-12 text-center"
          >
            <div className="w-20 h-20 mx-auto bg-red-50 rounded-full flex items-center justify-center mb-4">
              <Heart className="w-10 h-10 text-red-300" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Your wishlist is empty</h2>
            <p className="text-slate-500 mb-6 max-w-sm mx-auto">
              Save items you love by clicking the heart icon on any product
            </p>
            <Button 
              onClick={() => navigate('/products')}
              className="bg-[#0047AB] hover:bg-[#003380]"
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              Browse Products
            </Button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
            <AnimatePresence mode="popLayout">
              {wishlist.map((product, index) => {
                const stockBadge = getStockBadge(product);
                const isOutOfStock = product.stock <= 0;
                const discountPercent = product.original_price && product.original_price > product.price
                  ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
                  : 0;

                return (
                  <motion.div
                    key={product.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.2, delay: index * 0.03 }}
                    className={`bg-white rounded-xl sm:rounded-2xl shadow-sm overflow-hidden border border-slate-100 group ${
                      isOutOfStock ? 'opacity-70' : ''
                    } ${selectMode && selectedItems.has(product.id) ? 'ring-2 ring-[#0047AB]' : ''}`}
                    data-testid={`wishlist-item-${product.id}`}
                  >
                    {/* Image */}
                    <div 
                      className="relative aspect-square overflow-hidden cursor-pointer"
                      onClick={() => selectMode ? toggleSelectItem(product.id) : navigate(`/product/${product.id}`)}
                    >
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${
                          isOutOfStock ? 'grayscale' : ''
                        }`}
                        loading="lazy"
                      />
                      
                      {/* Select Checkbox */}
                      {selectMode && (
                        <div className={`absolute top-2 left-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          selectedItems.has(product.id) 
                            ? 'bg-[#0047AB] border-[#0047AB]' 
                            : 'bg-white/90 border-slate-300'
                        }`}>
                          {selectedItems.has(product.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </div>
                      )}
                      
                      {/* Remove Button */}
                      {!selectMode && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemove(product.id);
                          }}
                          className="absolute top-2 right-2 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-md hover:bg-red-50 transition-colors"
                          title="Remove from wishlist"
                          data-testid={`remove-wishlist-${product.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      )}

                      {/* Discount Badge */}
                      {discountPercent > 0 && (
                        <span className="absolute top-2 left-2 bg-green-500 text-white px-2 py-0.5 rounded-lg text-[10px] font-bold">
                          {discountPercent}% OFF
                        </span>
                      )}

                      {/* Stock Badge */}
                      {stockBadge && !discountPercent && (
                        <div className={`absolute top-2 left-2 ${stockBadge.color} text-white px-2 py-0.5 rounded-lg text-[10px] font-bold flex items-center gap-1`}>
                          <stockBadge.icon className="w-3 h-3" />
                          {stockBadge.text}
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="p-3 sm:p-4">
                      <div 
                        className="cursor-pointer"
                        onClick={() => !selectMode && navigate(`/product/${product.id}`)}
                      >
                        <p className="text-[10px] sm:text-xs text-[#0047AB] font-medium uppercase tracking-wider mb-1">
                          {product.category}
                        </p>
                        <h3 className="font-semibold text-slate-900 text-sm line-clamp-2 leading-5 min-h-[2.5rem] hover:text-[#0047AB] transition-colors">
                          {product.name}
                        </h3>
                      </div>

                      {/* Rating */}
                      {product.avg_rating > 0 && (
                        <div className="flex items-center gap-1 mt-1.5">
                          <div className="flex items-center gap-0.5 bg-green-600 text-white px-1.5 py-0.5 rounded text-[10px] font-bold">
                            <Star className="w-3 h-3 fill-current" />
                            {product.avg_rating.toFixed(1)}
                          </div>
                          <span className="text-[10px] text-slate-400">
                            ({product.rating_count})
                          </span>
                        </div>
                      )}
                      
                      {/* Price */}
                      <div className="mt-2">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-lg sm:text-xl font-bold text-slate-900">
                            ₹{product.price.toLocaleString()}
                          </p>
                          {product.original_price && product.original_price > product.price && (
                            <>
                              <p className="text-xs text-slate-400 line-through">
                                ₹{product.original_price.toLocaleString()}
                              </p>
                              <span className="text-xs text-[#f57224] font-semibold">
                                {Math.round(((product.original_price - product.price) / product.original_price) * 100)}% OFF
                              </span>
                            </>
                          )}
                        </div>
                        
                        {/* Delivery Date */}
                        <div className="flex items-center gap-1 mt-1.5 mb-3 text-[#0047AB]">
                          <Truck className="w-3.5 h-3.5" />
                          <span className="text-[10px] sm:text-xs font-medium">
                            Delivery by {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {!selectMode && (
                        <div className="flex gap-2">
                          {(() => {
                            const qty = getItemQuantity(product.id);
                            return qty > 0 ? (
                              <div className="flex-1 flex items-center justify-center bg-slate-100 border border-slate-200 rounded-md h-9">
                                <button
                                  onClick={(e) => handleDecrement(product, e)}
                                  className="flex-1 h-full flex items-center justify-center text-slate-700 hover:bg-slate-200 rounded-l-md transition-colors"
                                  data-testid={`decrement-wishlist-${product.id}`}
                                >
                                  <Minus className="w-3.5 h-3.5" />
                                </button>
                                <span className="w-8 text-center text-slate-900 font-bold text-sm">
                                  {qty}
                                </span>
                                <button
                                  onClick={(e) => handleIncrement(product, e)}
                                  className="flex-1 h-full flex items-center justify-center text-slate-700 hover:bg-slate-200 rounded-r-md transition-colors disabled:opacity-50"
                                  disabled={qty >= product.stock}
                                  data-testid={`increment-wishlist-${product.id}`}
                                >
                                  <Plus className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1 border-slate-200 text-slate-700 hover:bg-slate-50 text-xs h-9"
                                onClick={() => handleAddToCart(product)}
                                disabled={isOutOfStock}
                              >
                                <ShoppingCart className="w-3.5 h-3.5 mr-1" />
                                Add
                              </Button>
                            );
                          })()}
                          <Button
                            size="sm"
                            className="flex-1 bg-[#0047AB] hover:bg-[#003380] text-xs h-9"
                            onClick={() => handleMoveToCart(product)}
                            disabled={isOutOfStock}
                          >
                            Buy Now
                          </Button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
